import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  ALL_CATEGORIES,
  getProductsForCategory,
  getPartnersForProduct,
} from '../../data/outsourcePartners';
import { log } from '../../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Free-text fallback marker for the partner dropdown — surfaces a text
// input when the operator wants to enter a brand-new supplier that
// isn't in the curated list.
const FREE_TEXT_PARTNER = '__custom__';

const EMPTY_FORM = {
  category: '',
  product_type: '',
  material: '',
  roll_position: '',
  width: '',
  height: '',
  unit: 'inches',
  quantity: '',
  outsource_partner: '',
  outsource_partner_email: '',
  outsource_cost: '',
  custom_production_days: 5,
  sticker_limitations: '',
  admin_notes: '',
  total_price: '',
  currency: 'CAD',
};

const PARTNER_PICK_DEFAULT = '';

/**
 * Customer picker — searches admin/users/search as the operator types.
 * Debounce kept tight (250ms) since the endpoint is fast and the admin
 * is usually looking for a known customer.
 */
const CustomerPicker = ({ selectedUser, onSelect }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let canceled = false;
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API_URL}/api/admin/users/search`, {
          params: { q, limit: 10 },
        });
        if (!canceled) setResults(r.data?.users || []);
      } catch (err) {
        log.warn('User search failed:', err);
      }
    }, 250);
    return () => { canceled = true; clearTimeout(t); };
  }, [q]);

  return (
    <div className="relative">
      <Label className="text-sm font-semibold text-gray-700">Customer</Label>
      {selectedUser ? (
        <div className="flex items-center justify-between mt-1 bg-green-50 border border-green-200 rounded-md p-3">
          <div>
            <div className="font-medium text-gray-800">{selectedUser.full_name || '(no name)'}</div>
            <div className="text-xs text-gray-600">{selectedUser.email}</div>
          </div>
          <button
            type="button"
            onClick={() => onSelect(null)}
            data-testid="custom-order-clear-customer"
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <>
          <Input
            placeholder="Search by email or name…"
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            data-testid="custom-order-customer-search"
            className="mt-1"
          />
          {open && results.length > 0 && (
            <ul className="absolute z-50 w-full mt-1 max-h-56 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg">
              {results.map((u) => (
                <li
                  key={u.id}
                  onClick={() => { onSelect(u); setOpen(false); setQ(''); }}
                  data-testid={`custom-order-customer-row-${u.id}`}
                  className="px-3 py-2 hover:bg-green-50 cursor-pointer text-sm border-b last:border-b-0"
                >
                  <div className="font-medium">{u.full_name || '(no name)'}</div>
                  <div className="text-xs text-gray-500">{u.email}</div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
};

/**
 * CustomOrderModal — admin enters a custom order on behalf of a user.
 * Same render path is reused for editing (passes `existingOrder` prop).
 */
export const CustomOrderModal = ({ open, onClose, onSaved, existingOrder = null }) => {
  const isEdit = !!existingOrder;
  const [selectedUser, setSelectedUser] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [partnerSelection, setPartnerSelection] = useState(PARTNER_PICK_DEFAULT);
  const [saving, setSaving] = useState(false);
  // Partner rules registry — fetched once per modal open. Keyed by
  // partner display name. Drives the inline hint pill (production-day
  // override + dimension caps + material whitelist) so the operator
  // sees the constraints BEFORE submitting.
  const [partnerRulesByName, setPartnerRulesByName] = useState({});

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    axios.get(`${API_URL}/api/admin/partners/rules`)
      .then((r) => {
        if (!alive) return;
        const map = {};
        for (const p of r.data?.partners || []) {
          map[p.name] = p;
        }
        setPartnerRulesByName(map);
      })
      .catch((e) => log.debug('[CustomOrder] partner-rules fetch failed:', e?.name));
    return () => { alive = false; };
  }, [open]);

  // Hydrate when opened (or when switching between create/edit)
  useEffect(() => {
    if (!open) return;
    if (existingOrder) {
      const it = existingOrder.items?.[0] || {};
      setSelectedUser({
        id: existingOrder.user_id,
        email: existingOrder.customer_email || existingOrder.user_email || '',
        full_name: existingOrder.customer_name || '',
      });
      setForm({
        category: it.category || '',
        product_type: it.product_type || '',
        material: it.material || '',
        roll_position: it.roll_position || '',
        width: it.width || '',
        height: it.height || '',
        unit: it.unit || 'inches',
        quantity: it.quantity || '',
        outsource_partner: it.outsource_partner || '',
        outsource_partner_email: it.outsource_partner_email || '',
        outsource_cost: it.outsource_cost || '',
        custom_production_days: it.custom_production_days || 5,
        sticker_limitations: it.sticker_limitations || '',
        admin_notes: it.admin_notes || '',
        total_price: existingOrder.total_price || '',
        currency: existingOrder.currency || 'CAD',
      });
      setPartnerSelection(
        it.outsource_partner ? `${it.outsource_partner}|${it.outsource_partner_email}` : '',
      );
    } else {
      setSelectedUser(null);
      setForm(EMPTY_FORM);
      setPartnerSelection(PARTNER_PICK_DEFAULT);
    }
  }, [open, existingOrder]);

  const productOptions = useMemo(
    () => getProductsForCategory(form.category),
    [form.category],
  );
  const partnerOptions = useMemo(
    () => getPartnersForProduct(form.category, form.product_type),
    [form.category, form.product_type],
  );

  const onChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const onPartnerChange = (value) => {
    setPartnerSelection(value);
    if (value === FREE_TEXT_PARTNER) {
      onChange('outsource_partner', '');
      onChange('outsource_partner_email', '');
      return;
    }
    if (!value) {
      onChange('outsource_partner', '');
      onChange('outsource_partner_email', '');
      return;
    }
    const [name, email] = value.split('|');
    onChange('outsource_partner', name || '');
    onChange('outsource_partner_email', email || '');
  };

  // Per-label price preview — operator's choice 6a (simple total/qty)
  const pricePerLabel = useMemo(() => {
    const total = parseFloat(form.total_price);
    const qty = parseInt(form.quantity, 10);
    if (!total || !qty) return null;
    return total / qty;
  }, [form.total_price, form.quantity]);

  const buildPayload = () => ({
    user_id: selectedUser?.id,
    currency: form.currency,
    total_price: parseFloat(form.total_price),
    item: {
      category: form.category,
      product_type: form.product_type,
      material: form.material || '',
      roll_position: form.roll_position || '',
      width: parseFloat(form.width),
      height: parseFloat(form.height),
      unit: form.unit,
      quantity: parseInt(form.quantity, 10),
      outsource_partner: form.outsource_partner || '',
      outsource_partner_email: form.outsource_partner_email || '',
      outsource_cost: parseFloat(form.outsource_cost) || 0,
      custom_production_days: parseInt(form.custom_production_days, 10) || 5,
      sticker_limitations: form.sticker_limitations || '',
      admin_notes: form.admin_notes || '',
    },
  });

  const validate = () => {
    if (!selectedUser?.id) return 'Please select a customer.';
    if (!form.category) return 'Please pick a category.';
    if (!form.product_type) return 'Please pick a product type.';
    if (!form.width || parseFloat(form.width) <= 0) return 'Width must be > 0.';
    if (!form.height || parseFloat(form.height) <= 0) return 'Height must be > 0.';
    if (!form.quantity || parseInt(form.quantity, 10) <= 0) return 'Quantity must be > 0.';
    if (!form.total_price || parseFloat(form.total_price) <= 0) return 'Total price must be > 0.';
    return null;
  };

  const onSave = async () => {
    const err = validate();
    if (err) { toast.warning(err); return; }
    setSaving(true);
    try {
      const payload = buildPayload();
      if (isEdit) {
        const { user_id: _ignored, ...rest } = payload;
        const r = await axios.patch(
          `${API_URL}/api/admin/custom-orders/${existingOrder.id}`,
          rest,
        );
        toast.success('Custom order updated');
        onSaved?.(r.data);
      } else {
        const r = await axios.post(`${API_URL}/api/admin/custom-orders`, payload);
        toast.success(`Custom order ${r.data.order_number} created`);
        onSaved?.(r.data);
      }
      onClose();
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Backend returns {partner_rule_violations: [...]} for rule-engine
      // failures — surface each violation as its own toast so operator
      // sees ALL the issues, not just the first.
      if (detail && typeof detail === 'object' && Array.isArray(detail.partner_rule_violations)) {
        detail.partner_rule_violations.forEach((msg) => toast.error(msg));
      } else {
        const msg = detail || e.message || 'Save failed.';
        toast.error(typeof msg === 'string' ? msg : 'Save failed.');
      }
      log.error('Custom order save:', e);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="custom-order-modal">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white z-10 px-6 py-4 border-b flex items-center justify-between">
          <h2 className="text-xl font-bold text-[#4A7C59]">
            {isEdit ? `Edit Custom Order ${existingOrder?.order_number || ''}` : '+ New Custom Order'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700" data-testid="custom-order-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {!isEdit && (
            <CustomerPicker selectedUser={selectedUser} onSelect={setSelectedUser} />
          )}

          {/* Category + Product */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-semibold text-gray-700">Category</Label>
              <Select
                value={form.category}
                onValueChange={(v) => { onChange('category', v); onChange('product_type', ''); onPartnerChange(''); }}
              >
                <SelectTrigger className="mt-1" data-testid="custom-order-category">
                  <SelectValue placeholder="Pick category…" />
                </SelectTrigger>
                <SelectContent>
                  {ALL_CATEGORIES.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-semibold text-gray-700">Product Type</Label>
              <Select
                value={form.product_type}
                onValueChange={(v) => { onChange('product_type', v); onPartnerChange(''); }}
                disabled={!form.category}
              >
                <SelectTrigger className="mt-1" data-testid="custom-order-product-type">
                  <SelectValue placeholder={form.category ? 'Pick product…' : 'Pick category first'} />
                </SelectTrigger>
                <SelectContent>
                  {productOptions.map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Material + Roll position */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Material</Label>
              <Input value={form.material} onChange={(e) => onChange('material', e.target.value)} placeholder="e.g. Holographic, BOPP, …" data-testid="custom-order-material" />
            </div>
            <div>
              <Label>Roll Position <span className="text-xs text-gray-500">(if applicable)</span></Label>
              <Input value={form.roll_position} onChange={(e) => onChange('roll_position', e.target.value)} placeholder="e.g. left-out, right-in" data-testid="custom-order-roll-position" />
            </div>
          </div>

          {/* W / H / Unit / Quantity */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <Label>Width</Label>
              <Input type="number" step="0.1" min="0.1" value={form.width} onChange={(e) => onChange('width', e.target.value)} data-testid="custom-order-width" />
            </div>
            <div>
              <Label>Height</Label>
              <Input type="number" step="0.1" min="0.1" value={form.height} onChange={(e) => onChange('height', e.target.value)} data-testid="custom-order-height" />
            </div>
            <div>
              <Label>Unit</Label>
              <Select value={form.unit} onValueChange={(v) => onChange('unit', v)}>
                <SelectTrigger className="mt-1" data-testid="custom-order-unit"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="inches">inches</SelectItem>
                  <SelectItem value="cm">cm</SelectItem>
                  <SelectItem value="mm">mm</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Quantity</Label>
              <Input type="number" min="1" value={form.quantity} onChange={(e) => onChange('quantity', e.target.value)} data-testid="custom-order-quantity" />
            </div>
          </div>

          {/* Outsource Partner */}
          <div>
            <Label>Outsource Partner</Label>
            <Select value={partnerSelection} onValueChange={onPartnerChange}>
              <SelectTrigger className="mt-1" data-testid="custom-order-partner-select">
                <SelectValue placeholder={partnerOptions.length ? 'Pick partner…' : 'No curated partners — use free-text'} />
              </SelectTrigger>
              <SelectContent>
                {partnerOptions.map((p) => (
                  <SelectItem key={`${p.name}|${p.email}`} value={`${p.name}|${p.email}`}>
                    {p.name} <span className="text-xs text-gray-500">— {p.email}</span>
                  </SelectItem>
                ))}
                <SelectItem value={FREE_TEXT_PARTNER}>Custom (free-text)…</SelectItem>
              </SelectContent>
            </Select>
            {partnerSelection === FREE_TEXT_PARTNER && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                <Input placeholder="Partner name" value={form.outsource_partner} onChange={(e) => onChange('outsource_partner', e.target.value)} data-testid="custom-order-partner-name-free" />
                <Input placeholder="Partner email" value={form.outsource_partner_email} onChange={(e) => onChange('outsource_partner_email', e.target.value)} data-testid="custom-order-partner-email-free" />
              </div>
            )}
            {/* Per-partner rule hint — surfaces production-day default,
                dimension caps, and material whitelist so operator
                doesn't have to memorize each partner's quirks. */}
            {partnerRulesByName[form.outsource_partner] && (
              <div
                className="mt-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900"
                data-testid={`custom-order-partner-rules-${form.outsource_partner.replace(/\s+/g, '-')}`}
              >
                <div className="font-semibold mb-1">{form.outsource_partner} capabilities</div>
                <div className="grid grid-cols-2 gap-1">
                  <div>📅 Production: <strong>{partnerRulesByName[form.outsource_partner].production_days}d</strong></div>
                  <div>📏 Size: <strong>{partnerRulesByName[form.outsource_partner].min_dim_in}″–{partnerRulesByName[form.outsource_partner].max_dim_in}″</strong></div>
                  {Array.isArray(partnerRulesByName[form.outsource_partner].materials) && (
                    <div className="col-span-2">
                      🎨 Materials: <strong>{partnerRulesByName[form.outsource_partner].materials.join(', ')}</strong>
                    </div>
                  )}
                  {partnerRulesByName[form.outsource_partner].notes && (
                    <div className="col-span-2 text-emerald-800/90 italic mt-1">
                      {partnerRulesByName[form.outsource_partner].notes}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Outsource cost + Production days */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Outsource Cost <span className="text-xs text-gray-500">(CAD)</span></Label>
              <Input type="number" step="0.01" min="0" value={form.outsource_cost} onChange={(e) => onChange('outsource_cost', e.target.value)} data-testid="custom-order-outsource-cost" />
            </div>
            <div>
              <Label>Custom Production Days <span className="text-xs text-gray-500">(default: 5)</span></Label>
              <Input type="number" min="1" max="180" value={form.custom_production_days} onChange={(e) => onChange('custom_production_days', e.target.value)} data-testid="custom-order-production-days" />
            </div>
          </div>

          {/* Total Price + auto-calc per-label */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="font-semibold">Pricing TOTAL <span className="text-xs text-gray-500 font-normal">(taxes & shipping not included)</span></Label>
              <div className="flex gap-2 mt-1">
                <Select value={form.currency} onValueChange={(v) => onChange('currency', v)}>
                  <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CAD">CAD $</SelectItem>
                    <SelectItem value="USD">USD $</SelectItem>
                  </SelectContent>
                </Select>
                <Input type="number" step="0.01" min="0.01" value={form.total_price} onChange={(e) => onChange('total_price', e.target.value)} data-testid="custom-order-total-price" />
              </div>
            </div>
            <div className="flex flex-col justify-end">
              <div className="bg-green-50 border border-green-200 rounded-md p-3 text-center" data-testid="custom-order-price-per-label">
                <div className="text-xs text-gray-600">Your price per label</div>
                <div className="text-2xl font-bold text-[#4A7C59]">
                  {pricePerLabel != null ? `${form.currency} $${pricePerLabel.toFixed(4)}` : '—'}
                </div>
              </div>
            </div>
          </div>

          {/* Sticker Limitations */}
          <div>
            <Label>Sticker Limitations</Label>
            <Textarea rows={2} value={form.sticker_limitations} onChange={(e) => onChange('sticker_limitations', e.target.value)} placeholder="e.g. Max 4 colours. No metallic finishes. Min line weight 0.5pt." data-testid="custom-order-limitations" />
          </div>

          {/* Notes / More Options */}
          <div>
            <Label>Notes / More Options</Label>
            <Textarea rows={4} value={form.admin_notes} onChange={(e) => onChange('admin_notes', e.target.value)} placeholder="Free-form notes — operator-only. Phone-in details, special handling, billing reference, etc." data-testid="custom-order-notes" />
          </div>
        </div>

        <div className="sticky bottom-0 bg-white px-6 py-4 border-t flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} data-testid="custom-order-cancel-btn">Cancel</Button>
          <Button
            onClick={onSave}
            disabled={saving}
            className="bg-[#4A7C59] hover:bg-[#3D6A4A] text-white"
            data-testid="custom-order-save-btn"
          >
            {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Create order')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CustomOrderModal;
